
import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ProfileInfoForm } from "./ProfileInfoForm";
import { ProfilePasswordForm } from "./ProfilePasswordForm";

interface Profile {
  full_name: string;
  email: string;
  bio: string;
  phone_number?: string;
  phone_verified: boolean;
}

export const ProfileSection = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile>({
    full_name: "",
    email: "",
    bio: "",
    phone_number: "",
    phone_verified: false,
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    const { data: userResult, error: userError } = await supabase.auth.getUser();

    if (userError || !userResult?.user) {
      setProfile({
        full_name: "",
        email: "",
        bio: "",
        phone_number: "",
        phone_verified: false,
      });
      setLoading(false);
      return;
    }

    const userId = userResult.user.id;
    const email = userResult.user.email || "";

    const { data: profileData } = await supabase
      .from("profiles")
      .select("full_name, bio, phone_number, phone_verified")
      .eq("id", userId)
      .maybeSingle();

    setProfile({
      full_name: profileData?.full_name ?? "",
      email,
      bio: profileData?.bio ?? "",
      phone_number: profileData?.phone_number ?? "",
      phone_verified: profileData?.phone_verified ?? false,
    });

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="animate-spin h-6 w-6 mr-2" />
        Loading profile...
      </div>
    );
  }

  return (
    <div className="grid gap-8 md:grid-cols-2">
      <ProfileInfoForm profile={profile} loading={loading} onProfileReload={fetchProfile} />
      <ProfilePasswordForm />
    </div>
  );
};
